import { ReactNode, useCallback, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { CampaignStatus } from "@/enum/campaign-status.enum";

import {
	useLazyGetListCampaignSummaryQuery,
	useUpdateCampaignStatusMutation,
} from "@/pages/Private/redux/list/list.api";

import {
	CampaignStartFailure,
	CampaignToStart,
	describeStartFailure,
	resolveCampaignsToStart,
} from "./outreachActivation";
import { OutreachActivationModal, OutreachActivationPhase } from "./OutreachActivationModal";

type Choice = "start" | "assignOnly" | "cancel";

interface PromptState {
	campaigns: CampaignToStart[];
	phase: OutreachActivationPhase;
	failures: CampaignStartFailure[];
}

export interface AssignWithActivationParams {
	listId: number;
	/** The campaigns the assign puts leads into (duplicates and empty ids are ignored). */
	campaignIds: string[];
	/** The caller's own assign. Runs exactly once — unless the user cancels, then never. */
	assign: () => Promise<unknown>;
}

interface OutreachActivation {
	/**
	 * Runs a send-path assign, first asking to start any target campaign that is not sending
	 * (PREC-3296). Resolves `true` once the assign ran, `false` when the user cancelled — in which
	 * case nothing was written and the caller should leave its own UI as it was. A rejected assign
	 * rejects, as it would without the prompt.
	 */
	assignWithActivation: (params: AssignWithActivationParams) => Promise<boolean>;
	/** Render this wherever the caller renders its own modals. */
	activationModal: ReactNode;
}

/**
 * The one place a send path decides whether to start a campaign (PREC-3296). Starting is per
 * campaign (`PATCH list/:id/campaign/:uuid/status`), never the list-wide `campaignStatus`, which
 * would start every other non-paused campaign on the list along with the chosen one.
 *
 * The assign runs **before** the start: assignment always creates the lead's schedule rows and the
 * per-campaign send gate decides when they go, so the order cannot lose a lead — and a refused
 * start must not cost the user the assign they asked for. A refused start is reported in the modal
 * itself; nothing else would tell them (a rejected mutation is silent, PREC-4129).
 *
 * The status is read fresh on every call rather than from the row: the list a row carries is a
 * snapshot, and a stale status is how a prompt ends up offering to start a running campaign.
 */
export const useOutreachActivation = (): OutreachActivation => {
	const { t } = useTranslation();
	const [fetchCampaignSummary] = useLazyGetListCampaignSummaryQuery();
	const [updateCampaignStatus] = useUpdateCampaignStatusMutation();

	const [prompt, setPrompt] = useState<PromptState | null>(null);
	const resolveChoice = useRef<((choice: Choice) => void) | null>(null);
	const resolveAcknowledge = useRef<(() => void) | null>(null);

	// A summary that cannot be read must not block the assign the user asked for; it only means the
	// question is not asked this time, which is what happened on every send path before it existed.
	const findCampaignsToStart = useCallback(
		async (listId: number, campaignIds: string[]): Promise<CampaignToStart[]> => {
			try {
				const summary = await fetchCampaignSummary({ listId }, false).unwrap();

				return resolveCampaignsToStart(summary.data, campaignIds);
			} catch {
				return [];
			}
		},
		[fetchCampaignSummary]
	);

	const askUser = useCallback(
		(campaigns: CampaignToStart[]) =>
			new Promise<Choice>((resolve) => {
				resolveChoice.current = resolve;
				setPrompt({ campaigns, phase: "ask", failures: [] });
			}),
		[]
	);

	const startCampaigns = useCallback(
		async (listId: number, campaigns: CampaignToStart[]): Promise<CampaignStartFailure[]> => {
			// allSettled: one refused start must not abandon its siblings, but every refusal is kept.
			const results = await Promise.allSettled(
				campaigns.map((campaign) =>
					updateCampaignStatus({
						id: listId,
						campaignUuid: campaign.campaignId,
						status: CampaignStatus.RUNNING,
					}).unwrap()
				)
			);

			return results.flatMap((result, index) =>
				result.status === "rejected"
					? [
							{
								name: campaigns[index].name,
								reason: describeStartFailure(result.reason, t("outreachActivation.failedFallback")),
							},
						]
					: []
			);
		},
		[updateCampaignStatus, t]
	);

	const reportFailures = useCallback(
		(failures: CampaignStartFailure[]) =>
			new Promise<void>((resolve) => {
				resolveAcknowledge.current = resolve;
				setPrompt((current) => current && { ...current, phase: "failed", failures });
			}),
		[]
	);

	const assignWithActivation = useCallback(
		async ({ listId, campaignIds, assign }: AssignWithActivationParams): Promise<boolean> => {
			const campaigns = await findCampaignsToStart(listId, campaignIds);

			if (campaigns.length === 0) {
				await assign();

				return true;
			}

			const choice = await askUser(campaigns);

			if (choice === "cancel") {
				setPrompt(null);

				return false;
			}

			setPrompt((current) => current && { ...current, phase: "busy" });

			try {
				await assign();

				if (choice === "start") {
					const failures = await startCampaigns(listId, campaigns);

					if (failures.length > 0) {
						await reportFailures(failures);
					}
				}
			} finally {
				setPrompt(null);
			}

			return true;
		},
		[findCampaignsToStart, askUser, startCampaigns, reportFailures]
	);

	const choose = (choice: Choice) => {
		resolveChoice.current?.(choice);
		resolveChoice.current = null;
	};

	const acknowledge = () => {
		resolveAcknowledge.current?.();
		resolveAcknowledge.current = null;
	};

	const activationModal = prompt ? (
		<OutreachActivationModal
			campaigns={prompt.campaigns}
			failures={prompt.failures}
			phase={prompt.phase}
			onAcknowledge={acknowledge}
			onAssignOnly={() => choose("assignOnly")}
			onCancel={() => choose("cancel")}
			onStart={() => choose("start")}
		/>
	) : null;

	return { assignWithActivation, activationModal };
};
